import type { FormEvent } from "react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, ShieldCheck, Check } from "lucide-react";
import { API_BASE_URL } from '../utils/env';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase';
import { BRAND } from '@/brand';

const ACCENT = BRAND === 'corporate' ? '#E8890B' : '#1976D2';
const BRAND_NAME = BRAND === 'corporate' ? 'Run & Comply' : 'Grow & Raise';

function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");

    if (!email.trim() || !password.trim()) {
      setError("Please enter both email and password");
      return;
    }

    setLoading(true);

    // The owner account can never be permanently locked out of its own panel, but the
    // password is ALWAYS verified against Firebase Auth first (via /auth/login below).
    // There is deliberately no hardcoded password bypass — that would let an old password
    // keep working after it was changed.
    const isOwnerEmail = email.trim().toLowerCase() === "info@conncct.com";

    try {
      // 1. Backend login — verifies the password against Firebase Auth.
      const res = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await res.json();

      if (res.ok && data.success) {
        // Password verified by Firebase Auth at this point.
        const idToken = data.tokens?.idToken;
        const uid = data.user?.uid;

        if (idToken) {
          // Verify admin status
          const adminsRes = await fetch(`${API_BASE_URL}/auth/admins`, {
            headers: { Authorization: `Bearer ${idToken}` }
          });
          const adminsData = await adminsRes.json();

          const me = Array.isArray(adminsData.admins)
            ? adminsData.admins.find((a: any) => a.uid === uid || a.email === email)
            : null;
          const isAdmin = adminsData.success && !!me;

          // Grant access to real admins, and always to the owner account (so it can never
          // be locked out even if the admins list can't be read). Because we only reach
          // here after Firebase Auth accepted the password, the owner still has to enter
          // their CURRENT password — a changed-away old password will not work.
          if (isAdmin || isOwnerEmail) {
            // Sign into Firebase Auth client-side so the Firestore SDK has auth context.
            try {
              await signInWithEmailAndPassword(auth, email, password);
            } catch (fbErr) {
              console.warn('Firebase client auth sign-in failed (Firestore may be restricted):', fbErr);
            }

            const adminInfo = {
              email,
              role: isOwnerEmail ? 'super_admin' : ((me && me.role) || 'admin'), // real role → scopes the panel (perms.ts)
              loginTime: new Date().toISOString()
            };
            localStorage.setItem('adminUser', JSON.stringify(adminInfo));
            localStorage.setItem('isAdmin', 'true');
            localStorage.setItem('token', idToken);

            console.log('✅ Admin login successful');
            setLoading(false);
            navigate('/admin', { replace: true });
            return;
          }

          // Authenticated, but this account has no admin privileges.
          setLoading(false);
          setError("Access denied. This account does not have admin privileges.");
          return;
        }
      } else if (data.error) {
        console.log('❌ Backend login error:', data.error);
      }
    } catch (err) {
      console.log('⚠️ Admin login failed:', err);
    }

    // Wrong password, no such account, or the backend was unreachable.
    setLoading(false);
    setError("Access denied. Invalid admin credentials.");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1fr_1fr]" style={{ fontFamily: "'Montserrat',system-ui,sans-serif" }}>
      {/* Left — form */}
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-12 bg-white">
        <div className="max-w-md w-full mx-auto">
          <div className="flex items-center gap-2 mb-8 cursor-pointer" onClick={() => navigate('/')}>
            <span className="w-9 h-9 rounded-xl grid place-items-center text-white" style={{ background: ACCENT }}><ShieldCheck className="w-5 h-5" /></span>
            <div>
              <div className="text-[15px] font-black text-slate-900 leading-none">Conncct <span style={{ color: ACCENT }}>Admin</span></div>
              <div className="text-[10px] font-bold tracking-[0.14em] uppercase text-slate-400 mt-1">{BRAND_NAME}</div>
            </div>
          </div>

          <h1 className="text-2xl font-black text-slate-900 tracking-tight">Welcome back.</h1>
          <p className="text-sm text-slate-500 mt-1.5 mb-7">Sign in to manage clients, orders, documents and content.</p>

          {error && <div className="mb-4 text-sm font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1.5">Admin email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@conncct.com" autoComplete="email" disabled={loading}
                className="w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200 focus:border-slate-400" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-bold text-slate-600">Password</label>
                <Link to="/admin/forgot-password" className="text-xs font-bold" style={{ color: ACCENT }}>Forgot?</Link>
              </div>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Your password" autoComplete="current-password" disabled={loading}
                  className="w-full rounded-xl border border-slate-300 px-3.5 py-2.5 pr-10 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200 focus:border-slate-400" />
                <button type="button" onClick={() => setShowPassword(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" disabled={loading}>{showPassword ? <EyeOff size={16} /> : <Eye size={16} />}</button>
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} className="w-4 h-4 rounded" disabled={loading} />
              <span className="text-sm text-slate-600">Keep me signed in</span>
            </label>
            <button type="submit" disabled={loading} className="w-full rounded-xl py-3 text-sm font-bold text-white disabled:opacity-60" style={{ background: ACCENT }}>{loading ? 'Signing in…' : 'Sign in to the admin'}</button>
          </form>

          <div className="flex items-center justify-between mt-6 text-sm">
            <Link to="/login" className="text-slate-500 hover:text-slate-800 font-semibold">← Client login</Link>
            <Link to="/admin-signup" className="font-bold" style={{ color: ACCENT }}>First time? Create owner →</Link>
          </div>
        </div>
      </div>

      {/* Right — brand rail */}
      <div className="hidden lg:flex flex-col justify-center px-16 text-white relative overflow-hidden" style={{ background: 'linear-gradient(150deg, #17263A, #0E1A2B)' }}>
        <div aria-hidden style={{ position: 'absolute', width: 420, height: 420, borderRadius: '50%', top: -140, right: -120, background: `radial-gradient(circle, ${ACCENT}55, transparent 70%)`, filter: 'blur(14px)' }} />
        <div className="relative z-10 max-w-md">
          <div className="text-[11px] font-bold tracking-[0.18em] uppercase" style={{ color: ACCENT === '#1976D2' ? '#8fc0f5' : '#F5B841' }}>Conncct Admin · {BRAND_NAME}</div>
          <h2 className="text-3xl font-black tracking-tight mt-3 leading-tight">Run the whole back office from one panel.</h2>
          <p className="text-[15px] text-slate-300 mt-4 leading-relaxed">Clients, orders, documents, filings, pricing and content — managed and delivered by your team in one place.</p>
          <ul className="mt-8 space-y-3">
            {['Deliver documents & compliance calendars', 'Manage orders, billing and clients', 'Edit pricing & content — live to the site', 'Invite your team with the right access'].map(t => (
              <li key={t} className="flex items-center gap-3 text-sm text-slate-200"><span className="w-5 h-5 rounded-full grid place-items-center flex-none" style={{ background: ACCENT }}><Check className="w-3 h-3 text-white" /></span>{t}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default AdminLogin;
